import React, { useEffect, useState } from "react";
import "./blog.css";
import { miscService } from "../service/misc.service";
import { apiConstants } from "../constants/apiurl.constant";
import { ChevronRight, Loader2 } from "lucide-react";
import BlogModal from "./BlogModal";

export const Blogs = () => {
  const [selectedBlog, setSelectedBlog] = useState();
  const [blogData, setBlogData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [blogPage, setBlogPage] = useState(1);
  const [query, setQuery] = useState({
    pageVo: {
      pageNo: 1,
      noOfItems: 4,
    },
  });

  const blogsList = blogData?.rows || [];
  const totalPages = blogData.pages < 2 ? blogData?.pages : 2;

  useEffect(() => {
    getAllBlog(blogPage);
  }, [blogPage]);

  const getAllBlog = async (pageNo) => {
    try {
      setIsLoading(true);

      const response = await miscService.getAllBlogs({ 
        ...query, 
        pageVo: { ...query.pageVo, pageNo } 
      });

      if (response?.data?.rows) {
        setBlogData(response?.data);
        // console.log('blog data is', response?.data.rows);
      }
    } catch (e) {
      console.log(e, "this is e");
    } finally {
      setIsLoading(false);
    }
  };

  const [firstBlog, ...paginatedBlogs] = blogsList;

  // Loading Spinner Component
  const LoadingSpinner = () => (
    <div className="loading-container">
      <Loader2 className="loading-spinner" size={40} />
      <p className="loading-text">Loading blogs...</p>
    </div>
  );

  // Empty State Components
  const EmptyState = ({ message, className = "", showRetry = false }) => (
    <div className={`empty-state ${className}`}>
      <div className="empty-state-content">
        <div className="empty-state-icon">📝</div>
        <p className="empty-state-message">{message}</p>
        {showRetry && (
          <button 
            className="retry-btn"
            onClick={() => getAllBlog(blogPage)}
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );

  // Blog Skeleton for highlighted blog
  const HighlightBlogSkeleton = () => (
    <div className="hilightitem skeleton">
      <div className="hilightimgcontainer">
        <div className="skeleton-img"></div>
      </div>
      <div className="hilightcontent">
        <div className="skeleton-title"></div>
        <div className="skeleton-desc"></div>
        <div className="skeleton-desc short"></div>
        <div className="skeleton-readmore"></div>
      </div>
    </div>
  );

  // Blog Item Skeleton for paginated blogs
  const BlogItemSkeleton = () => (
    <div className="otheritems">
      <div className="blogitem skeleton">
        <div className="skeleton-thumbnail"></div>
        <div>
          <div className="skeleton-blog-title"></div>
          <div className="skeleton-readmore"></div>
        </div>
      </div>
    </div>
  );

  // Main loading state - show full page loading
  if (isLoading && blogsList.length === 0) {
    return (
      <div className="container" id="blogsList">
        <div className="blog-header-container">
          <div>
            <div className="title">Our Blog</div>
            <div className="desc">
              Read the articles, whitepapers and other stuff published and curated
              by our in-house editorial board.
            </div>
          </div>
        </div>
        
        <div className="bloglistcontainer">
          <HighlightBlogSkeleton />
          <div className="moreBlogs">
            {[1, 2, 3].map((index) => (
              <BlogItemSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No blogs available at all
  if (!isLoading && blogsList.length === 0) {
    return (
      <div className="container" id="blogsList">
        <div className="blog-header-container">
          <div>
            <div className="title">Our Blog</div>
            <div className="desc">
              Read the articles, whitepapers and other stuff published and curated
              by our in-house editorial board.
            </div>
          </div>
        </div>
        
        <EmptyState 
          message="No blogs available at the moment. Please check back later!" 
          className="main-empty-state"
          showRetry={true}
        />
      </div>
    );
  }

  // Safe get description function
  const getBlogDescription = (blog) => {
    try {
      return blog?.description?.blocks?.[0]?.text || "No description available";
    } catch (error) {
      return "No description available";
    }
  };

  return (
    <div className="container" id="blogsList">
      <div className="blog-header-container">
        <div>
          <div className="title">Our Blog</div>
          <div className="desc">
            Read the articles, whitepapers and other stuff published and curated
            by our in-house editorial board.
          </div>
        </div>
      </div>

      <div className="bloglistcontainer">
        {/* Highlighted Blog Section */}
        {firstBlog ? (
          <div data-aos="zoom-in" className="hilightitem">
            <div className="hilightimgcontainer">
              {firstBlog?.image ? (
                <img
                  src={`${apiConstants.imgUrl}${firstBlog.image}`}
                  alt={firstBlog.title || "Blog image"}
                  className="hilightimg"
                  onError={(e) => {
                    console.error("Image failed to load:", e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="no-image-placeholder">
                  <span>📝</span>
                </div>
              )}
            </div>

            <div onClick={() => setSelectedBlog(firstBlog)} className="hilightcontent">
              <div className="hilighttitle">{firstBlog?.title || "Untitled Blog"}</div>
              <div className="hilightdesc two-line-ellipsis">
                {getBlogDescription(firstBlog).slice(0, 80)}
                {getBlogDescription(firstBlog).length > 80 ? "....." : ""}
              </div>
              <div className="readmore">
                Read More <ChevronRight size={12} />
              </div>
            </div>
          </div>
        ) : (
          <EmptyState 
            message="No featured blog available" 
            className="highlight-empty-state"
            showRetry={true}
          />
        )}

        {/* Paginated Blogs Section */}
        <div className="moreBlogs">
          {isLoading && blogsList.length > 0 ? (
            // Show loading spinner while loading new page
            <LoadingSpinner />
          ) : paginatedBlogs && paginatedBlogs.length > 0 ? (
            // Show actual blogs
            paginatedBlogs.map((blog) => (
              <div key={blog?._id} className="otheritems">
                <div 
                  data-aos="zoom-in" 
                  onClick={() => setSelectedBlog(blog)} 
                  className="blogitem"
                >
                  {blog?.image ? (
                    <img
                      src={`${apiConstants.imgUrl}${blog?.image}`}
                      alt={blog?.title || "Blog thumbnail"}
                      className="blogthumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <span>📝</span>
                    </div>
                  )}
                  <div>
                    <div className="blogtitle">
                      {blog?.title || "Untitled Blog"}
                    </div>
                    <div className="readmore">
                      Read More <ChevronRight size={12} />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // No paginated blogs available
            <EmptyState 
              message="No more blogs available on this page" 
              className="paginated-empty-state"
            />
          )}

          {/* Pagination - only show if there are multiple pages */}
          {totalPages > 1 && (
            <div className="blog-pagination">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <span
                  key={idx}
                  className={`blog-dot${blogPage === (idx + 1) ? " active" : ""}`}
                  onClick={() => !isLoading && setBlogPage(idx + 1)}
                  style={{ 
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.5 : 1 
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <BlogModal
        show={!!selectedBlog}
        onClose={() => setSelectedBlog(null)}
        blog={selectedBlog || {}}
      />
    </div>
  );
};